import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './App.css'
// Только для standalone-оболочки демо — в Shadow DOM виджета не инлайнится.
// См. шапку файла: правки здесь не должны доезжать до клиентов.
import './styles/demo-shell.css'

// Initialize Sentry before rendering (must be first!)
import { initSentry } from './config/sentry'
import { AppErrorBoundary } from './components/ErrorBoundary'
import { resolveShowroomToken } from './services/showroomTokenResolver'
import { startBeacon } from './bootstrap/startBeacon'
import { config } from './config/runtimeConfig'

// Initialize Sentry with config from runtimeConfig / env vars
// Will gracefully skip if no DSN is provided
initSentry()

// Если в URL есть ?token=xxx (showroom-ссылка на лендинг/print-template),
// подтягиваем organizationId/inventorySlug/theme клиента ДО первого рендера,
// чтобы CarsContext сразу запросил правильный inventory, и UI получил
// корректную тему без моргания.
//
// Resolver сам ограничен timeout-ом (см. showroomTokenResolver.js); тут добавляем
// дополнительный safety hard cap на render — даже при сбое в самом resolver
// (например необработанный синхронный throw) первый рендер всё равно произойдёт.
const RENDER_HARD_CAP_MS = 6000
const root = ReactDOM.createRoot(document.getElementById('root'))
const renderApp = () => root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>
)

// DEV/preview convenience (#1108): standalone `/#/andys` has no ?token, so it
// would fall back to the demo build's default org (Autodis) — wrong logo +
// OMODA inventory. `/andys` IS the Andys landing, so seed the Andys org / slug /
// logo when opened without a token, making the preview faithful. No-op when a
// ?token is present (token wins) or on any other route. Does NOT affect the
// production widget-view overlay (that resolves via the Andys frontend token).
function seedAndysPreviewDefaults() {
  try {
    if (typeof window === 'undefined') return
    const hash = window.location.hash || ''
    const hasToken = new URLSearchParams(window.location.search).has('token')
    if (hasToken || !hash.startsWith('#/andys')) return
    window.b2bMountParams = {
      organizationId: '3',
      inventorySlug: 'demo-andy',
      widgetStyle: 'andys',
      dealerLogoUrl: '/assets/dealers/andys-logo.png',
      ...(window.b2bMountParams || {}),
    }
  } catch {
    /* preview-only convenience — never block boot */
  }
}
seedAndysPreviewDefaults()

// Сохраняем timeoutId чтобы очистить на ранний exit и не оставлять висящий
// таймер в фоне после render.
let renderTimeoutId
const renderTimeout = new Promise((resolve) => {
  renderTimeoutId = setTimeout(resolve, RENDER_HARD_CAP_MS)
})
/**
 * Analytics beacon for the STANDALONE shell (#1367).
 *
 * `initBeacon()` used to run only from `bootstrap.jsx init()` — the
 * `data-shiftgears` embed path — so everything served from this entry
 * (demo.shiftgears.ai, including the QR landing route `#/cars/:id/landing`)
 * emitted **zero** `analytics` events. Measured on prod: Andys, embedded via
 * `data-shiftgears` on its own site, had 344k beacon rows; Autodis, reachable
 * only through this shell, had 0 — while the same page loads still produced
 * browser-control WebSocket traffic. A QR scan was therefore uncountable: the
 * `page_view` that carries the landing URL never fired.
 *
 * Started off `resolveShowroomToken()` itself rather than off the render race
 * below. The race is capped at RENDER_HARD_CAP_MS so the UI paints even when
 * the resolver stalls — but the resolver is what supplies `?token=` and with it
 * apiUrl + frontendToken, and `initBeacon` permanently self-disables when those
 * are missing (it returns before setting `initialized`, and nothing retries).
 * Hanging the beacon off the cap would mean a slow network silently costs the
 * whole session's analytics, which is exactly the QR traffic being measured.
 *
 * Idempotent against the embed path: `initBeacon` warns and returns if already
 * initialised, and `trackPageView` de-dupes consecutive same-URL views.
 */
const showroomToken = resolveShowroomToken()

showroomToken.finally(() => {
  startBeacon({ apiUrl: config.CAR_API_URL, frontendToken: config.FRONTEND_TOKEN })
})

Promise.race([showroomToken, renderTimeout])
  .finally(() => {
    clearTimeout(renderTimeoutId)
    renderApp()
  })
